import { isUndefined } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';

import type { LogicCondition, LogicSubCondition, LogicVariant } from '@webapp/common/entities/logic';
import { initialLogicVariant } from '@webapp/common/entities/logic';
import type { QuestionRow } from '@webapp/common/entities/question-row';
import { parseJSON, undefDestroy } from '@webapp/common/lib/utils';
import type {
    LogicBoolFunc,
    LogicTransition,
    LogicTransitionType,
    LogicTransitionVariant,
} from '@webapp/common/resources/survey';
import { LogicType } from '@webapp/common/resources/survey';

export interface LogicConditionResponse {
    AFTER_QUESTION_ID: number;
    ANSWERS: string;
    TRANSITION_ACTION: string;
    BOOL_FUNC: LogicBoolFunc;
    BREAK_TEXT: string;
    COMPLETE_TEXT: string;
    GOAL: LogicTransition;
    ID: number;
    NEXT_PAGE: number;
    NEXT_QUESTION: number;
    NEXT_QUESTIONS: string;
    ANSWERS_TO_HIDE: string;
    LINK_PARAMS_LOGIC_TYPE: number;
    NEXT_PAGES: string;
    PARAMS: string; // JSON
    REWRITE: string;
    TO_SURVEY: number;
    TO_WEB: string;
    FINISH_TRANSITION: number;
    FINISH_TRANSITION_VARIANT: LogicTransitionVariant;
    TRANSITION_TYPE: LogicTransitionType;
    TYPE: LogicType;
    RESPONSES_COUNT: number;
    LINK_PARAM_ID: number;
    IS_EXTEDNED?: boolean;
    conditions: Array<any>;
    COPY_URL_PARAMS: boolean;
}

export const mapVariant = ({ ANSWER, ID, ORDER }: { ANSWER: string; ID: number; ORDER: number }): LogicVariant => {
    const name = parseJSON<Array<string>>(ANSWER, null);

    return {
        ...cloneDeep(initialLogicVariant),
        id: ID,
        name: name ? name[0] : ANSWER,
        checked: false,
        order: ORDER,
    };
};

export const mapQuestionRow = ({ value, id, order }: QuestionRow): LogicVariant => {
    return {
        ...cloneDeep(initialLogicVariant),
        id: id,
        name: value as string,
        checked: false,
        order,
    };
};
export const reverseMapCondition = (condition: Partial<LogicCondition>): any =>
    undefDestroy({
        responses_count: condition.responsesCount,
        transition_action: condition.action,
        next_question: condition.toQuestion,
        next_page: condition.toPage,
        type: condition.respondent,
        goal: condition.transition,
        bool_func: condition.boolFunc,
        complete_text: condition.completeText,
        transition_type: condition.transitionType,
        to_web: condition.toWebsite,
        break_text: condition.disqualText,
        to_survey: condition.toSurvey,
        isExtended: condition.isExtended,
        linkParamId: condition.linkParamId,
        finish_transition: condition.finishTransition,
        finish_transition_variant: condition.finishTransitionVariant,
        after_question_id: condition.afterQuestionId,
        link_params_logic_type: condition.linkParamsLogicType,
        next_questions: condition.toQuestions ? JSON.stringify(condition.toQuestions || []) : undefined,
        next_pages: condition.toPages ? JSON.stringify(condition.toPages || []) : undefined,
        answers_to_hide: condition.answersToHide ? JSON.stringify(condition.answersToHide || []) : undefined,
        copy_url_params: condition.copyUrlParams,
        params:
            condition.respondent === LogicType.SELECT_COUNT
                ? JSON.stringify({
                      variants: [],
                      minCount: condition.minCount,
                      maxCount: condition.maxCount,
                  })
                : undefined,
    });

export const reverseMapConditionNew = (condition: Partial<LogicSubCondition>): any =>
    undefDestroy({
        question_id: condition.questionId,
        responsesCount: condition.responsesCount,
        linkParamId: condition.linkParamId,
        boolFunc: condition.boolFunc,
        boolFuncExternal: condition.boolFuncExternal,
        type: condition.respondent,
        link_param_value: condition.linkParamValue,
        condition_type: condition.type,
        params:
            isUndefined(condition.variants) && isUndefined(condition.minCount) && isUndefined(condition.maxCount)
                ? undefined
                : JSON.stringify({
                      variants: condition.variants ?? [],
                      minCount: condition.minCount,
                      maxCount: condition.maxCount,
                  }),
    });
